// Pure helpers behind the station detail sheet (StationDetailView.swift):
// next-tide selection, chart domains, ticks and path geometry. No React or
// DOM imports so Vitest can run these in its node environment.
import type { TideExtreme, TidePredictionPoint } from '../../types/stations';

/**
 * StationDetailView.nextTideSummary: the first `count` high/low events at or
 * after `now`. When nothing is upcoming the first `count` events are used
 * instead so the summary never disappears while the chart still has data.
 */
export function selectNextTides(extremes: readonly TideExtreme[], now: Date, count = 4): TideExtreme[] {
  const nowMs = now.getTime();
  const upcoming = extremes.filter((e) => e.date.getTime() >= nowMs);
  return (upcoming.length > 0 ? upcoming : extremes).slice(0, count);
}

/** Swift `String(format: "%.1f", value)`: always one decimal, so 1.0 stays "1.0". */
export function formatFeet(value: number): string {
  return value.toFixed(1);
}

/** Swift `String(format: "%.4f°", degrees)`. */
export function formatDegrees(degrees: number): string {
  return `${degrees.toFixed(4)}°`;
}

export interface TimeDomain {
  start: Date;
  end: Date;
}

/** Time extent of the plotted series (curve first, extremes as a fallback). */
export function timeDomain(curve: readonly TidePredictionPoint[], extremes: readonly TideExtreme[]): TimeDomain | null {
  const source = curve.length > 0 ? curve : extremes;
  if (source.length === 0) return null;
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const p of source) {
    const t = p.date.getTime();
    if (t < min) min = t;
    if (t > max) max = t;
  }
  if (max === min) max = min + 60 * 60 * 1000;
  return { start: new Date(min), end: new Date(max) };
}

/** Smallest "nice" step (1, 2, 2.5, 5 × 10^n) that is at least `rawStep`. */
export function niceStep(rawStep: number): number {
  if (!Number.isFinite(rawStep) || rawStep <= 0) return 0.5;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  for (const m of [1, 2, 2.5, 5, 10]) {
    const step = m * magnitude;
    if (step >= rawStep - 1e-9) return step;
  }
  return 10 * magnitude;
}

export interface ValueDomain {
  min: number;
  max: number;
  step: number;
  ticks: number[];
}

const roundTo = (x: number, decimals: number) => Number(x.toFixed(decimals));

/**
 * Y domain snapped outward to nice tick boundaries. Tide heights can be
 * negative relative to MLLW, so zero is not forced into the range (Swift
 * Charts auto-scales the same way for LineMark).
 */
export function valueDomain(values: readonly number[], targetTicks = 4): ValueDomain | null {
  if (values.length === 0) return null;
  let lo = Number.POSITIVE_INFINITY;
  let hi = Number.NEGATIVE_INFINITY;
  for (const v of values) {
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  const step = niceStep(hi > lo ? (hi - lo) / targetTicks : 0.5);
  const min = roundTo(Math.floor(lo / step + 1e-9) * step, 6);
  let max = roundTo(Math.ceil(hi / step - 1e-9) * step, 6);
  if (max <= min) max = roundTo(min + step, 6);
  const ticks: number[] = [];
  for (let i = 0; ; i++) {
    const tick = roundTo(min + i * step, 6);
    if (tick > max + 1e-9) break;
    ticks.push(tick);
  }
  return { min, max, step, ticks };
}

/** Local midnights inside [start, end] (Swift `AxisMarks(values: .stride(by: .day))`). */
export function dayTicks(start: Date, end: Date): Date[] {
  const ticks: Date[] = [];
  const d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  if (d.getTime() < start.getTime()) d.setDate(d.getDate() + 1);
  while (d.getTime() <= end.getTime()) {
    ticks.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return ticks;
}

export interface LinearScale {
  (value: number): number;
  invert(pixel: number): number;
}

/** d3-style linear scale from [d0, d1] to [r0, r1]. A zero-width domain maps to r0. */
export function linearScale(d0: number, d1: number, r0: number, r1: number): LinearScale {
  const span = d1 - d0;
  const scale = ((value: number) => (span === 0 ? r0 : r0 + ((value - d0) / span) * (r1 - r0))) as LinearScale;
  scale.invert = (pixel: number) => (r1 === r0 ? d0 : d0 + ((pixel - r0) / (r1 - r0)) * span);
  return scale;
}

export interface Point {
  x: number;
  y: number;
}

const px = (n: number) => (Math.round(n * 100) / 100).toString();

/**
 * Uniform Catmull-Rom spline through `points`, emitted as cubic Béziers
 * (Swift `.interpolationMethod(.catmullRom)`). Endpoints are clamped.
 */
export function catmullRomPath(points: readonly Point[]): string {
  const n = points.length;
  if (n === 0) return '';
  const first = points[0];
  let d = `M${px(first.x)},${px(first.y)}`;
  for (let i = 0; i < n - 1; i++) {
    const p0 = points[Math.max(i - 1, 0)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(i + 2, n - 1)];
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C${px(c1x)},${px(c1y)} ${px(c2x)},${px(c2y)} ${px(p2.x)},${px(p2.y)}`;
  }
  return d;
}

/** Index of the sorted `times` entry closest to `t` (binary search). */
export function nearestIndex(times: readonly number[], t: number): number {
  const n = times.length;
  if (n === 0) return -1;
  let lo = 0;
  let hi = n - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (times[mid] < t) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && Math.abs(times[lo - 1] - t) <= Math.abs(times[lo] - t)) return lo - 1;
  return lo;
}

/** The high/low event within `toleranceMs` of `t`, if any (for the hover readout). */
export function extremeNear(extremes: readonly TideExtreme[], t: number, toleranceMs: number): TideExtreme | null {
  let best: TideExtreme | null = null;
  let bestDelta = toleranceMs;
  for (const e of extremes) {
    const delta = Math.abs(e.date.getTime() - t);
    if (delta <= bestDelta) {
      best = e;
      bestDelta = delta;
    }
  }
  return best;
}

const monthDay = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });

export function formatMonthDay(d: Date): string {
  return monthDay.format(d);
}

/** Accessible summary for the chart's aria-label. */
export function describeTideChart(
  stationName: string,
  curve: readonly TidePredictionPoint[],
  extremes: readonly TideExtreme[],
  time: TimeDomain,
  values: ValueDomain,
): string {
  const highs = extremes.filter((e) => e.isHigh).length;
  const lows = extremes.length - highs;
  let lo = Number.POSITIVE_INFINITY;
  let hi = Number.NEGATIVE_INFINITY;
  for (const p of curve) {
    if (p.value < lo) lo = p.value;
    if (p.value > hi) hi = p.value;
  }
  for (const e of extremes) {
    if (e.value < lo) lo = e.value;
    if (e.value > hi) hi = e.value;
  }
  const range = Number.isFinite(lo) && Number.isFinite(hi) ? `ranging from ${formatFeet(lo)} to ${formatFeet(hi)} ft` : `on a ${formatFeet(values.min)} to ${formatFeet(values.max)} ft axis`;
  return (
    `Tide predictions for ${stationName}: hourly curve from ${formatMonthDay(time.start)} to ${formatMonthDay(time.end)}, ` +
    `${range} above MLLW, with ${highs} high and ${lows} low tides marked.`
  );
}
